import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import SchemaOrg from '@/components/base/SchemaOrg';
import { getComicById, getRelatedComics } from '@/mocks/comics';
import NotFound from '@/pages/NotFound';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const comic = id ? getComicById(id) : undefined;

  if (!comic) {
    return <NotFound />;
  }

  const relatedComics = getRelatedComics(comic, 6);

  const formatViews = (views: number): string => {
    if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
    return views.toString();
  };

  const formatFavorites = (fav: number): string => {
    if (fav >= 10000) return `${(fav / 10000).toFixed(1)}万`;
    return fav.toString();
  };

  return (
    <>
      <SEOHead
        title={`${comic.title} - ${comic.categoryName}韩国漫画无删减下拉式阅读 - 韩漫网址`}
        description={`${comic.title}：${comic.description} 韩国漫画${comic.categoryName}题材，由${comic.author}创作，${comic.status}，评分${comic.rating}。免费韩漫在线阅读，无删减下拉式阅读体验尽在韩漫网址。`}
        keywords={`${comic.title},韩国漫画,${comic.categoryName}韩漫,无删减漫画,下拉式漫画,免费韩漫`}
        canonicalUrl={`https://hanman.example.com/article/${comic.id}`}
        ogImage={comic.coverImage}
        ogType="article"
        articlePublishedTime={comic.createdAt}
        articleModifiedTime={comic.updatedAt}
      />
      <SchemaOrg type="Article" data={{
        headline: comic.title,
        description: comic.description,
        image: comic.coverImage,
        author: { '@type': 'Person', name: comic.author },
        datePublished: comic.createdAt,
        dateModified: comic.updatedAt,
        publisher: { '@type': 'Organization', name: '韩漫网址' },
        mainEntityOfPage: { '@type': 'WebPage', '@id': `https://hanman.example.com/article/${comic.id}` },
      }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="bg-background-100 border-b border-background-200/70">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
              <Breadcrumb items={[
                { label: '首页', href: '/' },
                { label: `${comic.categoryName}韩漫`, href: `/category/${comic.category}` },
                { label: comic.title },
              ]} />
            </div>
          </div>

          <section className="py-8 md:py-12 px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-col lg:flex-row gap-8">
                <div className="lg:w-[300px] flex-shrink-0">
                  <div className="sticky top-24">
                    <div className="rounded-xl overflow-hidden border border-background-200/70">
                      <img
                        src={comic.coverImage}
                        alt={`${comic.title} - ${comic.categoryName}韩国漫画`}
                        title={`${comic.title} - 韩国漫画无删减下拉式阅读`}
                        className="w-full aspect-[3/4] object-cover object-top"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                      comic.status === '已完结'
                        ? 'bg-accent-100 text-accent-700'
                        : 'bg-primary-100 text-primary-700'
                    }`}>
                      {comic.status}
                    </span>
                    <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700">
                      {comic.categoryName}
                    </span>
                  </div>

                  <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
                    {comic.title}
                  </h1>

                  <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-foreground-600">
                    <span className="flex items-center gap-1">
                      <i className="ri-user-line"></i>
                      {comic.author}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-star-fill text-accent-500"></i>
                      <strong className="text-foreground-900">{comic.rating}</strong>
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i>
                      {formatViews(comic.views)} 阅读
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-heart-line"></i>
                      {formatFavorites(comic.favorites)} 收藏
                    </span>
                  </div>

                  <div className="mt-4 flex flex-wrap gap-2">
                    {comic.tags.map(tag => (
                      <span key={tag} className="px-2.5 py-1 rounded-md text-xs bg-background-100 text-foreground-600 border border-background-200/70">
                        #{tag}
                      </span>
                    ))}
                  </div>

                  <div className="mt-6 p-5 rounded-xl bg-background-100 border border-background-200/70">
                    <h2 className="text-base font-semibold text-foreground-900 mb-2 font-heading">作品简介</h2>
                    <p className="text-sm text-foreground-700 leading-relaxed">{comic.longDescription}</p>
                  </div>

                  <div className="mt-6">
                    <div className="flex items-center justify-between mb-3">
                      <h2 className="text-base font-semibold text-foreground-900 font-heading">章节列表</h2>
                      <span className="text-xs text-foreground-500">共 {comic.chapters.length} 话</span>
                    </div>
                    <div className="rounded-xl border border-background-200/70 overflow-hidden">
                      {comic.chapters.map((ch, index) => (
                        <div
                          key={ch.id}
                          className={`flex items-center justify-between px-5 py-3.5 hover:bg-background-100 transition-colors cursor-pointer ${
                            index < comic.chapters.length - 1 ? 'border-b border-background-200/70' : ''
                          }`}
                        >
                          <span className="text-sm text-foreground-800 font-medium">{ch.title}</span>
                          <span className="text-xs text-foreground-500 whitespace-nowrap">{ch.updatedAt}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {relatedComics.length > 0 && (
            <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
              <div className="max-w-7xl mx-auto">
                <div className="mb-8">
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
                    相关推荐
                  </h2>
                  <p className="mt-2 text-sm text-foreground-600">
                    你可能也会喜欢这些韩国漫画
                  </p>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
                  {relatedComics.map(c => (
                    <ComicCard key={c.id} comic={c} />
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}